import React, { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { extractVendorId } from "../utils/vendorSlug";
import { storeUrlForDevice } from "../config/appStores";

/**
 * "Open in the Lusuku app" — the bridge between a scanned QR and the app.
 * ────────────────────────────────────────────────────────────────────────────
 * Someone scans a product QR. Three things can be true, and all three have to
 * work:
 *
 *   THEY HAVE THE APP, and the OS honours app links. The URL never reaches the
 *   browser — Android App Links / iOS Universal Links open the app directly.
 *   This banner is never seen. That is the best case and it needs no code
 *   here, only the native config.
 *
 *   THEY HAVE THE APP, but the OS opened the browser anyway (app links not yet
 *   verified, an in-app browser like WhatsApp's, a desktop-to-phone hop).
 *   Tapping the banner fires the custom scheme and the app takes over.
 *
 *   THEY DO NOT HAVE THE APP. The web page they are already looking at IS the
 *   product page, so nothing is lost. Tapping the banner sends them to the
 *   store; when they come back to this tab and tap again, the app opens on
 *   this exact product.
 *
 * WHY NOT AN AUTOMATIC REDIRECT. `DeepLinkHandler` (the restaurant flow)
 * navigates to the scheme immediately on mount and then to the store after
 * 2s. That is hostile on a page that already renders the content: a visitor
 * who does not want the app gets bounced to a store listing they did not ask
 * for, and on iOS the scheme attempt raises a modal error when the app is
 * absent. A banner is opt-in, so the page stays usable either way.
 *
 * There is no deferred deep linking here — no fingerprinting, no paste-board
 * matching. Those are unreliable and privacy-hostile; the "come back and tap
 * again" path costs one tap and always works.
 */
const SCHEME = "com.lusuku:/";

/* Web path → app path. They are deliberately identical strings today (see
   `linking.js` in the apps), so this is a pass-through with an allowlist —
   an unmapped path must NOT be handed to the app, or it opens on nothing. */
const APP_LINKABLE = [
  /^\/brand\/shop\/[^/]+\/product\/[^/]+$/,
  /^\/brand\/shop\/[^/]+$/,
  /^\/restaurant\/[^/]+$/,
];

const OpenInAppBanner = ({ label = "Open in the Lusuku app" }) => {
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);

  const appUrl = useMemo(() => {
    const path = location.pathname.replace(/\/+$/, "");
    if (!APP_LINKABLE.some((re) => re.test(path))) return null;
    /* The web spells ids with a readable slug ("made-in-prc--<id>"); app
       builds from before the slug-aware deep links read the segment as the
       id, so the app is always handed the bare id. */
    const appPath = path.split("/").map((segment) => extractVendorId(segment)).join("/");
    return `${SCHEME}${appPath}`;
  }, [location.pathname]);

  const isMobile = useMemo(
    () => /Android|iPad|iPhone|iPod/i.test(navigator.userAgent || ""),
    []
  );

  const open = useCallback(() => {
    if (!appUrl) return;
    const store = storeUrlForDevice();

    /* If the app is installed the scheme navigation takes the tab away and
       this page is backgrounded — so the store hop never runs. If it is not,
       nothing happens and the timer fires. `visibilitychange` is what tells
       the two apart; a bare setTimeout would send app owners to the store as
       well. */
    let settled = false;
    const onHide = () => {
      settled = true;
      document.removeEventListener("visibilitychange", onHide);
    };
    document.addEventListener("visibilitychange", onHide);

    window.location.href = appUrl;
    setTimeout(() => {
      document.removeEventListener("visibilitychange", onHide);
      if (!settled && !document.hidden) window.location.href = store;
    }, 1200);
  }, [appUrl]);

  // Desktop visitors cannot open the app; showing them a store link is noise.
  if (!appUrl || !isMobile || dismissed) return null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 14px",
        background: "#111827",
        color: "white",
        position: "sticky",
        top: 0,
        zIndex: 1200,
      }}
    >
      <div style={{ flex: 1, minWidth: 0, fontSize: 13, lineHeight: 1.35 }}>
        {label}
        <div style={{ opacity: 0.7, fontSize: 11 }}>
          Faster ordering, order tracking and receipts.
        </div>
      </div>
      <button
        type="button"
        onClick={open}
        style={{
          background: "#7C3AED",
          color: "white",
          border: "none",
          borderRadius: 999,
          padding: "8px 16px",
          fontWeight: 700,
          fontSize: 13,
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        Open
      </button>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
        style={{
          background: "transparent",
          border: "none",
          color: "rgba(255,255,255,0.6)",
          fontSize: 18,
          lineHeight: 1,
          cursor: "pointer",
          padding: 4,
          flexShrink: 0,
        }}
      >
        ×
      </button>
    </div>
  );
};

export default OpenInAppBanner;
